import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import * as dashboardService from '@/api/dashboardService';
import type { simulation } from '@/features/dashboard/types.api';
import PageLayout from '@/components/layout/pageLayout/PageLayout';
import AppHeader from '@/components/layout/header/AppHeader';
import Spinner from '@/components/common/spinner/Spinner';
import AlertMessage from '@/components/layout/message/AlertMessage';
import Card from '@/components/common/card/Card';
import { PlayCircle, ChevronRight } from 'lucide-react';
import styles from './StudentSimulations.module.css';

const StudentSimulations: React.FC = () => {
    const [simulations, setSimulations] = useState<simulation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        document.body.classList.add('solid-public-bg');
        return () => document.body.classList.remove('solid-public-bg');
    }, []);

    useEffect(() => {
        const fetchSimulations = async () => {
            if (!user) {
                return;
            }
            try {
                setIsLoading(true);
                setError(null);
                const data = await dashboardService.getSimulations(user.id.toString());

                const virtualSimulations = data.filter(
                    sim => sim.type === 'virtual' && sim.state === 'closed'
                );

                setSimulations(virtualSimulations);
            } catch (err) {
                console.error("Depuración: Error en el fetch:", err);
                setError("Hubo un error al cargar los simulacros disponibles.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchSimulations();
    }, [user]);

    const handleSelectSimulation = (simulationId: number | string) => {
        navigate(`/student/virtual-simulations/${simulationId}`);
    };

    const mainContent = (
        <div className={styles.container}>
            <div className={styles.layout}>
                <aside className={styles.intro}>
                    <span className={styles.kicker}>Simulacros virtuales</span>
                    <h1 className={styles.title}>Presentar Simulacro Virtual</h1>
                    <p className={styles.subtitle}>
                        Selecciona uno de los simulacros disponibles para comenzar.
                        Cada uno replica la estructura y los tiempos de una prueba real.
                    </p>
                </aside>

                <div className={styles.cardsCol}>
                    {isLoading && <Spinner text="Buscando simulacros..." />}

                    {error && <AlertMessage type="error">{error}</AlertMessage>}

                    {!isLoading && !error && simulations.length === 0 && (
                        <AlertMessage type="info">
                            No tienes simulacros virtuales asignados en este momento.
                        </AlertMessage>
                    )}

                    {!isLoading && !error && simulations.length > 0 && (
                        <div className={styles.simulationsList}>
                            {simulations.map((sim) => (
                                <Card key={sim.id} className={styles.simulationCard}>
                                    <button
                                        className={styles.cardButton}
                                        onClick={() => handleSelectSimulation(sim.id)}
                                    >
                                        <div className={styles.cardIconWrapper}>
                                            <PlayCircle size={28} className={styles.cardIcon} />
                                        </div>
                                        <div className={styles.cardBody}>
                                            <h2 className={styles.cardTitle}>{sim.name}</h2>
                                            {sim.description && (
                                                <p className={styles.cardDescription}>
                                                    {sim.description}
                                                </p>
                                            )}
                                        </div>
                                        <div className={styles.cardAction}>
                                            <span>Comenzar</span>
                                            <ChevronRight size={18} />
                                        </div>
                                    </button>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <PageLayout
            header={<AppHeader backButtonPath="/student-home" />}
            main={mainContent}
        />
    );
};

export default StudentSimulations;
